/**
 * 市场品种本地图标映射
 * 优先级：本地图标 → 数据库 logo → 字母占位符
 *
 * 资源目录：
 *   国旗:   @/assets/img/flags/{code}.svg
 *   加密货币: @/assets/img/crypto/{symbol}.svg
 *   商品/贵金属: @/assets/img/commodity/{name}.svg
 *   股票:   @/assets/img/stock/{ticker}.svg
 */

// ========== 1. 外汇：货币代码 → 国旗 ==========
const currencyToFlag = {
  'USD': 'us',  'EUR': 'eu',  'GBP': 'gb',  'JPY': 'jp',
  'AUD': 'au',  'NZD': 'nz',  'CAD': 'ca',  'CHF': 'ch',
  'CNY': 'cn',  'CNH': 'cn',  'HKD': 'hk',  'SGD': 'sg',
  'SEK': 'se',  'DKK': 'dk',  'NOK': 'no',  'MXN': 'mx',
  'ZAR': 'za',  'TRY': 'tr',  'RUB': 'ru',  'INR': 'in',
  'BRL': 'br',  'KRW': 'kr',  'TWD': 'tw',  'THB': 'th',
  'MYR': 'my',  'IDR': 'id',  'PHP': 'ph',  'PLN': 'pl',
  'HUF': 'hu',  'CZK': 'cz',  'ILS': 'il',  'CLP': 'cl',
  'ARS': 'ar',  'COP': 'co',  'PEN': 'pe',  'SAR': 'sa',
  'AED': 'ae',  'QAR': 'qa',  'KWD': 'kw',  'BHD': 'bh',
  'OMR': 'om',  'JOD': 'jo',  'EGP': 'eg',  'NGN': 'ng',
  'KES': 'ke',  'GHS': 'gh',  'MAD': 'ma',  'TND': 'tn',
  'VND': 'vn',  'PKR': 'pk',  'BDT': 'bd',  'LKR': 'lk',
  'MMK': 'mm',  'KHR': 'kh',  'LAK': 'la',  'RON': 'ro',
  'BGN': 'bg',  'HRK': 'hr',  'ISK': 'is',  'UAH': 'ua',
  'GEL': 'ge',  'AMD': 'am',  'AZN': 'az',  'KZT': 'kz',
  'UZS': 'uz',
};

// ========== 2. 加密货币：symbol → 本地 SVG 文件名 ==========
const cryptoIcons = {
  'BTC': 'btc',   'ETH': 'eth',   'BNB': 'bnb',   'SOL': 'sol',
  'XRP': 'xrp',   'ADA': 'ada',   'DOGE': 'doge',  'LINK': 'link',
  'UNI': 'uni',   'FTT': 'ftt',   'ARB': 'arb',
};

// ========== 3. 股票：ticker（去掉.US后缀）→ 本地 SVG 文件名 ==========
const stockIcons = {
  'AAPL': 'aapl.svg',   'AMZN': 'amzn.svg',   'GOOGL': 'googl.svg', 'MSFT': 'msft.svg',
  'TSLA': 'tsla.svg',   'NVDA': 'nvda.svg',   'INTC': 'intc.svg',   'BIDU': 'bidu.svg',
  'NTES': 'ntes.svg',   'SHEL': 'shel.svg',   'SPCX': 'spcx.png',
};

// ========== 4. 商品/贵金属/期货：品种代码 → 本地 SVG 文件名 ==========
const commodityIcons = {
  'GOLD': 'gold',     'SILVER': 'silver',     'PLATINUM': 'platinum',
  'PALLADIUM': 'palladium', 'COPPER': 'copper', 'ALUMINUM': 'aluminum',
  'NICKEL': 'nickel', 'ZINC': 'zinc',         'LEAD': 'lead',
  'USOIL': 'usoil',  'UKOIL': 'ukoil',       'NGAS': 'ngas',
};

// ========== 5. 基金：港股代码 → 本地 SVG 文件名 ==========
const fundIcons = {
  '02800': 'fund-02800.svg', '02823': 'fund-02823.svg',
  '03033': 'fund-03033.svg', '03067': 'fund-03067.svg',
  '02828': 'fund-02828.svg', '03037': 'fund-03037.svg',
  '02840': 'fund-02840.svg', '03188': 'fund-03188.svg',
};

/**
 * 从品种代码中提取外汇国旗
 */
function getForexFlag(code) {
  if (!code) return null;
  const upper = code.toUpperCase();
  const base = upper.substring(0, 3);
  const quote = upper.substring(3, 6);
  if (base === 'USD' && currencyToFlag[quote]) return currencyToFlag[quote];
  if (currencyToFlag[base]) return currencyToFlag[base];
  if (currencyToFlag[quote]) return currencyToFlag[quote];
  return null;
}

/**
 * 根据品种代码获取本地图标路径（统一入口）
 * 支持外汇国旗、加密货币、商品/贵金属
 * @param {string} code - 品种代码
 * @returns {string|null} webpack 处理后的图片路径，找不到返回 null
 */
export function getMarketFlagSrc(code) {
  if (!code) return null;
  const upper = code.toUpperCase();

  // 1. 商品/贵金属/期货 — 完整匹配
  if (commodityIcons[upper]) {
    try { return require(`@/assets/img/commodity/${commodityIcons[upper]}.svg`); } catch (e) { /* skip */ }
  }

  // 2. 股票 — 去掉 .US / .HK 等后缀后匹配 ticker
  const ticker = upper.replace(/\.(US|HK|SH|SZ|UK|JP)$/, '');
  if (stockIcons[ticker]) {
    try { return require(`@/assets/img/stock/${stockIcons[ticker]}`); } catch (e) { /* skip */ }
  }

  // 3. 加密货币 — 去掉末尾 USDT/USD 后匹配 symbol
  const symbol = upper.replace(/USDT$|USD$/, '');
  if (cryptoIcons[symbol]) {
    try { return require(`@/assets/img/crypto/${cryptoIcons[symbol]}.svg`); } catch (e) { /* skip */ }
  }

  // 4. 基金 — 港股代码直接匹配
  if (fundIcons[upper]) {
    try { return require(`@/assets/img/logo/${fundIcons[upper]}`); } catch (e) { /* skip */ }
  }

  // 5. 外汇 — 货币对拆分匹配国旗
  const flagCode = getForexFlag(code);
  if (flagCode) {
    try { return require(`@/assets/img/flags/${flagCode}.svg`); } catch (e) { /* skip */ }
  }

  return null;
}

export { currencyToFlag, cryptoIcons, stockIcons, commodityIcons, fundIcons, getForexFlag };
export default currencyToFlag;
